"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, Stethoscope, CalendarPlus, Menu, X, Info, Phone, Newspaper, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Locale } from "@/i18n-config";
import { getLocalizedPath } from "@/lib/slugs";

interface MobileNavDict {
    home?: string;
    services?: string;
    book?: string;
    book_now?: string;
    contact?: string;
    menu?: string;
    about?: string;
    blog?: string;
}

function normalizeWhatsAppNumber(value?: string) {
    if (!value) return "";
    return value.replace(/[^\d]/g, "");
}

function WhatsAppGlyph({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
            <path d="M17.47 14.38c-.3-.15-1.74-.86-2.01-.96-.27-.1-.47-.15-.66.15-.2.3-.76.96-.93 1.15-.17.2-.34.22-.63.08-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.13.3-.34.44-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.66-1.6-.9-2.19-.24-.57-.48-.5-.66-.51l-.56-.01c-.2 0-.51.07-.78.37-.27.3-1.02 1-1.02 2.43 0 1.43 1.05 2.82 1.2 3.01.15.2 2.07 3.16 5.02 4.43.7.3 1.25.48 1.67.62.7.22 1.34.19 1.85.12.56-.08 1.74-.71 1.98-1.4.24-.68.24-1.27.17-1.4-.07-.12-.27-.2-.56-.34z" />
            <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.05 2zm0 18.15a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.24 8.24 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.23 8.23z" />
        </svg>
    );
}

export default function MobileNav({ lang, dict, whatsappNumber }: { lang: Locale, dict: MobileNavDict, whatsappNumber?: string }) {
    const pathname = usePathname();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const normalizedWhatsAppNumber = normalizeWhatsAppNumber(whatsappNumber) || "905412340044";
    const homeHref = lang === "tr" ? "/" : `/${lang}`;

    const isActive = (path: string) => pathname?.includes(path);
    const isHome = pathname === "/" || pathname === `/${lang}`;

    const menuItems = [
        { label: dict.about, href: getLocalizedPath("about-us", lang), Icon: Info },
        { label: dict.contact, href: getLocalizedPath("contact-us", lang), Icon: Phone },
        { label: dict.blog, href: getLocalizedPath("blog", lang), Icon: Newspaper },
    ];

    return (
        <>
            {/* Floating dark bottom navigation */}
            <div className="fixed inset-x-3 bottom-3 z-50 lg:hidden [padding-bottom:env(safe-area-inset-bottom)]">
                <div className="flex items-center justify-between gap-0.5 rounded-[22px] border border-white/10 bg-[#111826]/95 px-1.5 py-1.5 shadow-[0_14px_36px_rgba(5,12,30,0.45)] backdrop-blur-xl">
                    {/* Home */}
                    <Link
                        href={homeHref}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex flex-1 flex-col items-center gap-1 rounded-xl py-1"
                    >
                        <span className="flex h-8 items-center justify-center">
                            <Home className={`h-[22px] w-[22px] transition-colors ${isHome ? "text-white" : "text-white/60"}`} />
                        </span>
                        <span className={`text-[10px] font-medium leading-none ${isHome ? "text-white" : "text-white/55"}`}>{dict.home}</span>
                    </Link>

                    {/* Treatments */}
                    <Link
                        href={getLocalizedPath("services", lang)}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex flex-1 flex-col items-center gap-1 rounded-xl py-1"
                    >
                        <span className="flex h-8 items-center justify-center">
                            <Stethoscope className={`h-[22px] w-[22px] transition-colors ${isActive("services") ? "text-white" : "text-white/60"}`} />
                        </span>
                        <span className={`text-[10px] font-medium leading-none ${isActive("services") ? "text-white" : "text-white/55"}`}>{dict.services}</span>
                    </Link>

                    {/* Book Now (highlighted) */}
                    <Link
                        href={getLocalizedPath("book-now", lang)}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex flex-1 flex-col items-center gap-1 rounded-xl py-1"
                    >
                        <span className="flex h-8 w-8 items-center justify-center rounded-[11px] bg-gradient-to-br from-[#2f6bed] to-[#1d4fd0] text-white shadow-md shadow-[#1d4fd0]/30 transition-transform active:scale-95">
                            <CalendarPlus className="h-[18px] w-[18px]" />
                        </span>
                        <span className="text-[10px] font-semibold leading-none text-white">{dict.book_now || dict.book}</span>
                    </Link>

                    {/* Menu */}
                    <button
                        type="button"
                        onClick={() => setIsMenuOpen((prev) => !prev)}
                        aria-label={dict.menu}
                        aria-expanded={isMenuOpen}
                        className="flex flex-1 flex-col items-center gap-1 rounded-xl py-1"
                    >
                        <span className="flex h-8 items-center justify-center">
                            {isMenuOpen ? (
                                <X className="h-[22px] w-[22px] text-white" />
                            ) : (
                                <Menu className="h-[22px] w-[22px] text-white/60" />
                            )}
                        </span>
                        <span className={`text-[10px] font-medium leading-none ${isMenuOpen ? "text-white" : "text-white/55"}`}>{dict.menu}</span>
                    </button>

                    {/* WhatsApp (highlighted) */}
                    <Link
                        href={`https://wa.me/${normalizedWhatsAppNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setIsMenuOpen(false)}
                        className="flex flex-1 flex-col items-center gap-1 rounded-xl py-1"
                    >
                        <span className="flex h-8 w-8 items-center justify-center rounded-[11px] bg-gradient-to-br from-[#25D366] to-[#12a150] text-white shadow-md shadow-[#12a150]/30 transition-transform active:scale-95">
                            <WhatsAppGlyph className="h-[19px] w-[19px]" />
                        </span>
                        <span className="text-[10px] font-semibold leading-none text-white">WhatsApp</span>
                    </Link>
                </div>
            </div>

            {/* Menu Drawer (triggered by Menu button) */}
            {isMenuOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] lg:hidden"
                    onClick={() => setIsMenuOpen(false)}
                >
                    <div
                        className="absolute inset-x-3 bottom-[84px] overflow-hidden rounded-[20px] border border-black/5 bg-white p-1.5 shadow-[0_18px_44px_rgba(5,12,30,0.28)] animate-in slide-in-from-bottom-6 fade-in duration-200 [margin-bottom:env(safe-area-inset-bottom)]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {menuItems.map(({ label, href, Icon }) => (
                            <Link
                                key={href}
                                href={href}
                                onClick={() => setIsMenuOpen(false)}
                                className="group flex items-center gap-3 rounded-2xl px-3 py-2.5 transition-colors hover:bg-[#f4f8fb] active:bg-[#eef4fa]"
                            >
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0f4c81]/8 text-[#0f4c81]">
                                    <Icon className="h-[18px] w-[18px]" />
                                </span>
                                <span className="flex-1 text-[15px] font-semibold text-[#17324d]">{label}</span>
                                <ChevronRight className="h-4 w-4 text-gray-300 transition-transform group-hover:translate-x-0.5" />
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}
